import {weakMemo} from '@packages/utilities/memo';
import {identity, constant} from '@packages/utilities/fp/utils';
import {
  type Type,
  Plugins,
  Types,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {type Expression, ERecord} from '@packages/systems/wfdl-ast';

// Before passing it to ExpressionEditor, we remove from data `styleBlockIds`,
// `editable` and `dyn`, and only keep the `style, `attr` and `search`
// data properties that are defined in the dataType.
//
// When handling actions from ExpressionEditor we put these pieces back.
//
// For a long term solution, we want to make sure that all these things are
// accounted for in `dataType`s or are using other WFDL APIs.

const hiddenProperties = ['editable', 'dyn'];

const getFieldsFor = (key: string) =>
  // @ts-expect-error - TS7006 - Parameter 'plugins' implicitly has an 'any' type.
  weakMemo((plugins) =>
    // @ts-expect-error - TS7006 - Parameter 'dataType' implicitly has an 'any' type.
    weakMemo((dataType) => {
      const fieldTy = Types.getRecordFields(
        Types.unwrapAliasType(dataType, plugins)
      )?.[key];
      return fieldTy
        ? Types.getRecordFields(Types.unwrapAliasType(fieldTy, plugins))
        : undefined;
    })
  );

const fieldsGetter = {
  attr: getFieldsFor('attr'),
  style: getFieldsFor('style'),
  macro: getFieldsFor('macro'),
} as const;

export const removeHiddenProperties: (
  plugins: Plugins.PluginsType
) => (arg1: Expression) => (arg1: Type) => Expression = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo(
      matchExpression<(arg1: Type) => Expression>({
        Record: (properties) =>
          weakMemo((dataType) => {
            return ERecord(
              Object.keys(properties).reduce<Record<string, any>>(
                (acc, key) => {
                  if (hiddenProperties.includes(key)) return acc;

                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly attr: (plugins: any) => (dataType: any) => { [key: string]: Type; } | null | undefined; readonly style: (plugins: any) => (dataType: any) => { [key: string]: Type; } | null | undefined; readonly macro: (plugins: any) => (dataType: any) => { ...; } | ... 1 more ... | undefined; }'.
                  const getFields = fieldsGetter[key];
                  if (getFields) {
                    const fields = getFields(plugins)(dataType);
                    acc[key] = matchExpression({
                      Record: (attrs) =>
                        ERecord(
                          Object.keys(attrs).reduce<Record<string, any>>(
                            (newAttrs, attrName) => {
                              if (fields?.[attrName])
                                newAttrs[attrName] = attrs[attrName];
                              return newAttrs;
                            },
                            {}
                          )
                        ),
                      _: identity,
                      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
                    })(properties[key]);
                  } else {
                    acc[key] = properties[key];
                  }
                  return acc;
                },
                {}
              )
            );
          }),
        _: constant,
      })
    )
);

export const addBackHiddenProperties: (
  plugins: Plugins.PluginsType
) => (arg1: Expression) => (arg2: Type) => (arg3: Expression) => Expression =
  weakMemo((plugins: Plugins.PluginsType) =>
    matchExpression<(arg1: Type) => (arg2: Expression) => Expression>({
      Record:
        (properties) => (dataType: Type) => (originalExpr: Expression) => {
          const originalProperties = Expressions.getRecord(originalExpr);
          const neededOriginalProperties = originalProperties
            ? Object.keys(originalProperties).reduce<Record<string, any>>(
                (acc, curr) => {
                  // If we have hidden the current field, and the incoming data does not show it
                  if (
                    hiddenProperties.includes(curr) &&
                    !properties.hasOwnProperty(curr)
                  ) {
                    acc[curr] = originalProperties[curr];
                    return acc;
                  }
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly attr: (plugins: any) => (dataType: any) => { [key: string]: Type; } | null | undefined; readonly style: (plugins: any) => (dataType: any) => { [key: string]: Type; } | null | undefined; readonly macro: (plugins: any) => (dataType: any) => { ...; } | ... 1 more ... | undefined; }'.
                  const getFields = fieldsGetter[curr];
                  if (getFields) {
                    const fields = getFields(plugins)(dataType);
                    acc[curr] = matchExpression({
                      Record: (attrs) => {
                        // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
                        const newAttr = Expressions.getRecord(properties[curr]);
                        return ERecord(
                          Object.keys(attrs).reduce<Record<string, any>>(
                            (attrAcc, attrCurr) => {
                              // If the incoming new data has this field, keep the value
                              if (newAttr && newAttr[attrCurr]) {
                                attrAcc[attrCurr] = newAttr[attrCurr];
                              } else if (!fields?.[attrCurr]) {
                                // if the given attribute is not defined in the dataType
                                // we have removed it, we want to put it back here from
                                // the original fields
                                attrAcc[attrCurr] = attrs[attrCurr];
                              }
                              return attrAcc;
                            },
                            {}
                          )
                        );
                      },
                      _: identity,
                      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
                    })(originalProperties[curr]);
                  }
                  return acc;
                },
                {}
              )
            : null;
          return ERecord({
            ...properties,
            ...neededOriginalProperties,
          });
        },
      _: (expr) => (_: Type) => (__: Expression) => expr,
    })
  );
